<div class="media-library-view container">
  <div class="page-header">
    <mat-button-toggle-group class="view-mode-toggle" [value]="viewMode()" (change)="setViewMode($event.value)" hideSingleSelectionIndicator="true">
      <mat-button-toggle value="grid" [matTooltip]="'media-library-view.view-mode.grid' | translate">
        <mat-icon>view_module</mat-icon>
      </mat-button-toggle>
      <mat-button-toggle value="table" [matTooltip]="'media-library-view.view-mode.table' | translate">
        <mat-icon>view_list</mat-icon>
      </mat-button-toggle>
    </mat-button-toggle-group>

    <button type="button" mat-flat-button color="primary" class="upload-button" (click)="openUploadModal()">
      <mat-icon>cloud_upload</mat-icon>
      <span>{{ "media-library-view.upload" | translate }}</span>
    </button>
  </div>

  <div class="search-bar">
    <mat-form-field class="search-input" appearance="outline" subscriptSizing="dynamic">
      <mat-icon matPrefix>search</mat-icon>
      <input matInput type="text" placeholder="{{ 'generic.search' | translate }}" [title]="'generic.search' | translate" [formControl]="searchControl" />
      @if (searchControl.value) {
        <button type="button" matSuffix mat-icon-button (click)="clearSearch()" [attr.aria-label]="'generic.clear' | translate">
          <mat-icon>close</mat-icon>
        </button>
      }
    </mat-form-field>

    <div class="selection-actions">
      <button type="button" mat-stroked-button class="action-button delete-button" [disabled]="!hasSelection() || actionLoading()" (click)="deleteSelected()">
        <mat-icon fontSet="material-symbols-outlined">delete</mat-icon>
        <span>{{ "media-library-view.actions.delete" | translate }}</span>
        @if (hasSelection()) {
          <span class="selection-count">({{ selectionCount() }})</span>
        }
      </button>
      <button
        type="button"
        mat-stroked-button
        class="action-button secondary-button"
        [disabled]="!hasSelection() || actionLoading()"
        [matMenuTriggerFor]="moveCategoryMenu">
        <mat-icon>drive_file_move_outline</mat-icon>
        <span>{{ "media-library-view.actions.move" | translate }}</span>
        @if (hasSelection()) {
          <span class="selection-count">({{ selectionCount() }})</span>
        }
      </button>
    </div>
  </div>

  <mat-menu #moveCategoryMenu="matMenu">
    @for (target of moveTargets(); track target.category) {
      <button type="button" mat-menu-item [matMenuTriggerFor]="moveCollectionMenu" (mouseenter)="setActiveMoveTarget(target)">
        {{ target.titleKey | translate }}
      </button>
    }
  </mat-menu>

  <mat-menu #moveCollectionMenu="matMenu">
    @if (activeMoveTarget(); as target) {
      <button type="button" mat-menu-item (click)="moveSelected(target.category, null)">
        {{ "media-library-view.move.root" | translate }}
      </button>
      @for (collection of target.collections; track collection.name) {
        <button type="button" mat-menu-item (click)="moveSelected(target.category, collection)">
          {{ collection.displayName }}
        </button>
      }
    }
  </mat-menu>

  @if (loading()) {
    <div class="loading-wrap">
      <mat-spinner [diameter]="48"></mat-spinner>
    </div>
  } @else {
    @for (section of sections(); track section.category) {
      <section class="category-section">
        <div class="section-header">
          <h2 class="section-title">{{ section.titleKey | translate }}</h2>
          <div class="create-folder">
            @if (creatingFolderCategory() !== section.category) {
              <button type="button" mat-stroked-button class="create-folder-button" (click)="startCreateFolder(section.category)">
                <mat-icon>create_new_folder</mat-icon>
                <span>{{ "media-library-view.create-folder.button" | translate }}</span>
              </button>
            } @else {
              <div class="create-folder-row">
                <mat-form-field class="create-folder-input" appearance="outline" subscriptSizing="dynamic">
                  <input
                    matInput
                    type="text"
                    [formControl]="folderNameControl"
                    placeholder="{{ 'media-library-view.create-folder.placeholder' | translate }}"
                    [title]="'media-library-view.create-folder.placeholder' | translate"
                    (keydown.enter)="confirmCreateFolder()"
                    (keydown.escape)="cancelCreateFolder()" />
                </mat-form-field>
                <button type="button" mat-flat-button class="primary-button" [disabled]="!canCreateFolder()" (click)="confirmCreateFolder()">
                  {{ "media-library-view.create-folder.confirm" | translate }}
                </button>
                <button type="button" mat-stroked-button class="secondary-button" (click)="cancelCreateFolder()">
                  {{ "generic.cancel" | translate }}
                </button>
              </div>
              @if (folderNameError()) {
                <div class="create-folder-error">{{ folderNameError()! | translate }}</div>
              }
            }
          </div>
        </div>

        @if (section.collections.length > 0) {
          <div class="collections-row">
            @for (collection of section.collections; track collection.name) {
              @if (renamingCollection()?.category === section.category && renamingCollection()?.name === collection.name) {
                <div class="rename-folder">
                  <div class="rename-folder-row">
                    <mat-form-field class="rename-folder-input" appearance="outline" subscriptSizing="dynamic">
                      <input
                        matInput
                        type="text"
                        [formControl]="renameNameControl"
                        placeholder="{{ 'media-library-view.rename-folder.placeholder' | translate }}"
                        [title]="'media-library-view.rename-folder.placeholder' | translate"
                        (keydown.enter)="confirmRenameFolder()"
                        (keydown.escape)="cancelRenameFolder()" />
                    </mat-form-field>
                    <button type="button" mat-flat-button class="primary-button" [disabled]="!canRenameFolder()" (click)="confirmRenameFolder()">
                      {{ "media-library-view.rename-folder.confirm" | translate }}
                    </button>
                    <button type="button" mat-stroked-button class="secondary-button" (click)="cancelRenameFolder()">
                      {{ "generic.cancel" | translate }}
                    </button>
                  </div>
                  @if (renameFolderError()) {
                    <div class="rename-folder-error">{{ renameFolderError()! | translate }}</div>
                  }
                </div>
              } @else {
                <div class="collection-card" [class.collection-card--active]="section.selectedCollection === collection.name">
                  <button type="button" class="collection-card__select" (click)="toggleCollection(section.category, collection.name)">
                    <mat-icon>folder</mat-icon>
                    <span class="collection-name">{{ collection.displayName }}</span>
                  </button>
                  @if (!collection.isTriggerbee) {
                    <button
                      type="button"
                      class="collection-rename-button"
                      [matTooltip]="'media-library-view.rename-folder.button' | translate"
                      (click)="startRenameFolder(section.category, collection.name)">
                      <mat-icon>edit</mat-icon>
                    </button>
                  }
                </div>
              }
            }
          </div>
        }

        @if (section.isEmpty) {
          <div class="empty-state">{{ "media-library-view.empty" | translate }}</div>
        } @else if (viewMode() === "grid") {
          <div class="media-grid">
            @for (item of section.visibleItems; track item.media.uri) {
              @if (section.category === MediaCategory.Videos) {
                <div
                  class="media-item media-item--video"
                  [class.media-item--selected]="item.isSelected"
                  [class.media-item--clickable]="item.selectable"
                  [matTooltip]="item.media.filename"
                  (click)="item.selectable && toggleSelection(item.media.uri)">
                  <mat-icon class="video-icon">play_circle</mat-icon>
                  <span class="video-name">{{ item.media.filename }}</span>
                  @if (item.selectable) {
                    <div class="select-indicator" [class.select-indicator--selected]="item.isSelected" aria-hidden="true">
                      <mat-icon>check_circle</mat-icon>
                    </div>
                  }
                </div>
              } @else {
                <div
                  class="media-item media-item--image"
                  [class.media-item--selected]="item.isSelected"
                  [class.media-item--clickable]="item.selectable"
                  [matTooltip]="item.media.filename"
                  (click)="item.selectable && toggleSelection(item.media.uri)">
                  <div class="media-item__thumb checkerboard">
                    <img
                      loading="lazy"
                      alt="{{ 'media-library-view.image-alt' | translate }}"
                      [src]="item.media.url"
                      [title]="item.media.filename"
                      onError="this.src='/assets/img/campaign/preview_no_image.png'" />
                  </div>
                  @if (item.selectable) {
                    <div class="select-indicator" [class.select-indicator--selected]="item.isSelected" aria-hidden="true">
                      <mat-icon>check_circle</mat-icon>
                    </div>
                  }
                </div>
              }
            }
          </div>
        } @else {
          <div class="media-table-wrap">
            <table class="media-table">
              <thead>
                <tr>
                  <th class="col-select" scope="col">
                    <span class="sr-only">{{ "media-library-view.actions.select" | translate }}</span>
                  </th>
                  <th class="col-preview" scope="col">
                    <span class="sr-only">{{ "media-library-view.table.preview" | translate }}</span>
                  </th>
                  <th class="col-filename" scope="col">{{ "media-library-view.table.filename" | translate }}</th>
                  <th class="col-size" scope="col">{{ "media-library-view.table.size" | translate }}</th>
                  <th class="col-date" scope="col">{{ "media-library-view.table.date" | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (item of section.visibleItems; track item.media.uri) {
                  <tr
                    [class.row--selected]="item.isSelected"
                    [class.row--clickable]="item.selectable"
                    (click)="item.selectable && toggleSelection(item.media.uri)">
                    <td class="col-select">
                      @if (item.selectable) {
                        <mat-checkbox
                          [checked]="item.isSelected"
                          (click)="$event.stopPropagation()"
                          (change)="toggleSelection(item.media.uri)"
                          [attr.aria-label]="'media-library-view.actions.select' | translate"></mat-checkbox>
                      }
                    </td>
                    <td class="col-preview">
                      @if (section.category === MediaCategory.Videos) {
                        <div class="table-thumb table-thumb--video">
                          <mat-icon>play_circle</mat-icon>
                        </div>
                      } @else {
                        <div class="table-thumb checkerboard">
                          <img
                            loading="lazy"
                            alt="{{ 'media-library-view.image-alt' | translate }}"
                            [src]="item.media.url"
                            [title]="item.media.filename"
                            onError="this.src='/assets/img/campaign/preview_no_image.png'" />
                        </div>
                      }
                    </td>
                    <td class="col-filename">
                      <span class="filename-text" [title]="item.media.filename">{{ item.media.filename }}</span>
                    </td>
                    <td class="col-size">{{ "media-library-view.size-not-available" | translate }}</td>
                    <td class="col-date">
                      @if (item.media.lastModified) {
                        {{ item.media.lastModified | date: "mediumDate" }}
                      } @else {
                        {{ "media-library-view.size-not-available" | translate }}
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }

        @if (section.hasMore) {
          <div class="show-more-row">
            <button type="button" mat-stroked-button class="secondary-button" (click)="showMore(section.category)">
              {{ "media-library-view.show-more" | translate }}
            </button>
          </div>
        }
      </section>
    }
  }
</div>
